import { gql } from '@apollo/client';
import React, { useMemo } from 'react';
import { sortBy } from 'ramda';
import classNames from 'classnames';
import { ColorSwatch } from '@moda/om';
import isTouchDevice from 'is-touch-device';
import { ColorSelectorFragment } from '../../generated/types/ColorSelectorFragment';
import { isDiscounted } from '../../lib/variantUtils/isDiscounted';

import './ColorSelector.scss';

export const COLOR_SELECTOR_VARIANT_FRAGMENT = gql`
  fragment ColorSelectorVariantFragment on VariantData {
    id
    color
    soldOut: sold_out
    prices(country_code: $countryCode) {
      originalPrice: original_price {
        price
      }
      currentPrice: current_price {
        price
      }
    }
  }
`;

export const COLOR_SELECTOR_FRAGMENT = gql`
  fragment ColorSelectorFragment on Variant {
    masterVariant: master_variants_data {
      ...ColorSelectorVariantFragment
    }
    otherVariants: other_variants_data {
      ...ColorSelectorVariantFragment
    }
  }
  ${COLOR_SELECTOR_VARIANT_FRAGMENT}
`;

interface Props {
  size?: 'default' | 'small';
  variant: ColorSelectorFragment;
  onSelect(color: string): void;
  onMouseEnter?(color: string): void;
  onMouseLeave?(): void;
  selected?: string | null;
  className?: string;
}

export const ColorSelector: React.FC<Props> = ({
  size,
  variant,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  selected,
  className
}) => {
  const colors = useMemo(
    () => sortBy(variant => variant?.id || 0, [variant.masterVariant, ...variant.otherVariants]),
    [variant.masterVariant, variant.otherVariants]
  );

  return (
    <div className={classNames('ColorSelector', className)}>
      {colors.map(
        variation =>
          variation &&
          variation.color && (
            <ColorSwatch
              size={size}
              className="ColorSelector__swatch"
              onClick={onSelect}
              onMouseEnter={isTouchDevice() ? undefined : onMouseEnter}
              onMouseLeave={isTouchDevice() ? undefined : onMouseLeave}
              key={variation.id}
              color={variation.color}
              title={variation.color}
              soldOut={variation.soldOut}
              selected={variation.color === selected}
              onSale={isDiscounted(variation.prices)}
            />
          )
      )}
    </div>
  );
};
